"use client";

import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useRef,
} from "react";
import { CircleHelp } from "lucide-react";
import { useAppPreferences } from "@/components/AccentColorProvider";
import type { AppReasoningStyle } from "@/lib/appPreferences";

const OPTIONS: Array<{
  value: AppReasoningStyle;
  label: string;
  description: string;
}> = [
  {
    value: "precision",
    label: "정확 중심",
    description: "검증된 근거를 우선하고 추정 위험요인 생성을 최소화합니다.",
  },
  {
    value: "balanced",
    label: "균형",
    description: "근거 중심 평가에 날씨·시간 등 유의미한 맥락 위험을 함께 검토합니다.",
  },
  {
    value: "exploratory",
    label: "탐색 중심",
    description: "날씨·시간·환경의 상호작용까지 폭넓게 탐색한 뒤 검증합니다.",
  },
];

const POSITION_CLASS: Record<AppReasoningStyle, string> = {
  precision: "-left-0.5",
  balanced: "left-1/2 -translate-x-1/2",
  exploratory: "-right-0.5",
};

const FILL_CLASS: Record<AppReasoningStyle, string> = {
  precision: "w-3",
  balanced: "w-1/2",
  exploratory: "w-full",
};

export function ReasoningStyleSelector() {
  const { reasoningStyle, saveReasoningStyle } = useAppPreferences();
  const sliderRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const suppressPointerClickRef = useRef(false);
  const optionIndex = OPTIONS.findIndex(
    (option) => option.value === reasoningStyle,
  );
  const selectedIndex = optionIndex >= 0 ? optionIndex : 1;
  const selected = OPTIONS[selectedIndex];

  const selectFromClientX = useCallback(
    (clientX: number) => {
      const bounds = sliderRef.current?.getBoundingClientRect();
      if (!bounds || bounds.width <= 0) return;
      const ratio = Math.min(
        1,
        Math.max(0, (clientX - bounds.left) / bounds.width),
      );
      const index = Math.min(2, Math.max(0, Math.round(ratio * 2)));
      void saveReasoningStyle(OPTIONS[index].value);
    },
    [saveReasoningStyle],
  );

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.preventDefault();
    draggingRef.current = true;
    suppressPointerClickRef.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    selectFromClientX(event.clientX);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    event.preventDefault();
    selectFromClientX(event.clientX);
  };

  const finishPointerDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    window.setTimeout(() => {
      suppressPointerClickRef.current = false;
    }, 0);
  };

  return (
    <div className="px-3 pb-3 pt-2.5">
      <div className="mb-1.5 flex items-center gap-1">
        <span className="whitespace-nowrap text-[10px] font-bold tracking-[-0.01em] text-gray-400 dark:text-gray-500">
          위험요인 탐색 성향
        </span>
        <div className="group relative flex shrink-0 items-center">
          <button
            type="button"
            aria-label="위험요인 탐색 성향 설명"
            className="flex h-3.5 w-3.5 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-seexr-primary/30 dark:text-gray-500 dark:hover:text-gray-300"
          >
            <CircleHelp size={12} strokeWidth={2} />
          </button>
          <div
            role="tooltip"
            className="pointer-events-none absolute bottom-[calc(100%+7px)] right-0 z-[120] w-[260px] translate-y-1 rounded-xl border border-gray-200 bg-white p-3 text-[11px] font-medium leading-[1.55] text-gray-600 opacity-0 shadow-xl transition-all duration-150 group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100 dark:border-[#343438] dark:bg-[#242427] dark:text-gray-300"
          >
            <div className="space-y-1.5">
              {OPTIONS.map((option) => (
                <p key={option.value}>
                  <span className="font-bold text-gray-900 dark:text-white">
                    {option.label}
                  </span>
                  <span className="text-gray-400 dark:text-gray-500"> · </span>
                  {option.description}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-1.5 flex items-center justify-center text-gray-800 dark:text-gray-100">
        <span className="text-[13px] font-bold">{selected.label}</span>
      </div>

      <div
        ref={sliderRef}
        role="radiogroup"
        aria-label="위험요인 탐색 성향"
        className="relative h-7 touch-none select-none cursor-grab active:cursor-grabbing"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishPointerDrag}
        onPointerCancel={finishPointerDrag}
        onLostPointerCapture={() => {
          draggingRef.current = false;
        }}
      >
        <div className="pointer-events-none absolute inset-x-0 top-1/2 h-5 -translate-y-1/2 overflow-hidden rounded-full border border-gray-200 bg-gray-100 dark:border-[#444449] dark:bg-[#353539]">
          <div
            className={`h-full rounded-full bg-seexr-primary transition-[width] duration-150 ${FILL_CLASS[reasoningStyle]}`}
          />
        </div>

        <div className="pointer-events-none absolute left-[9px] right-[9px] top-1/2 z-10 flex -translate-y-1/2 items-center justify-between">
          {OPTIONS.map((option, index) => (
            <span
              key={option.value}
              className={`h-1.5 w-1.5 rounded-full ${index <= selectedIndex ? "bg-white/55" : "bg-gray-400/80 dark:bg-gray-500"}`}
            />
          ))}
        </div>

        {OPTIONS.map((option) => {
          const active = option.value === reasoningStyle;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-label={option.label}
              aria-checked={active}
              onClick={(event) => {
                if (suppressPointerClickRef.current) {
                  event.preventDefault();
                  return;
                }
                void saveReasoningStyle(option.value);
              }}
              className={`absolute top-1/2 z-20 h-7 w-7 -translate-y-1/2 cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-seexr-primary/30 ${POSITION_CLASS[option.value]}`}
            >
              {active ? (
                <span className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border border-gray-200 bg-white shadow-[0_1px_4px_rgba(0,0,0,0.17)] dark:border-[#55555b] dark:bg-[#f4f4f5]" />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
